import { EnergyType } from "@/types/data";
import { sendGTMEvent } from "@next/third-parties/google";
import { isVictoria } from "./constants";

/**
 * Onboarding funnel stages, in order.
 *
 * Deliberately constants rather than the visible fieldset legends: a copy
 * change must not rename a funnel stage and orphan its history. The onboarding
 * form is one page today, but these map 1:1 onto the planned multi-step form,
 * so the same ids can carry over and keep the data comparable.
 *
 * The Address fieldset is deliberately absent. Every field in it is disabled —
 * it displays the address resolved during lookup and cannot be edited — so no
 * customer can ever engage with it. Listing it as a stage would report a
 * permanent 100% drop-off at Address and send whoever reads the funnel chasing
 * a problem that does not exist.
 */
export const ONBOARDING_SECTIONS = [
	"your_details",
	"identification",
	"billing_address_confirmation",
	"billing_delivery_method",
] as const;

export type OnboardingSectionId = (typeof ONBOARDING_SECTIONS)[number];

/**
 * Typed dataLayer contract. See docs/analytics-events.md.
 *
 * GTM triggers fire only on the event names below — never on DOM scraping or
 * element ids. Adding or changing an event here is a contract change and needs
 * a corresponding tag change in the container.
 */
export type AnalyticsEvent =
	| {
			// Client-side route changes only. The initial load and any full
			// browser load are genuine page views, which the container records.
			event: "virtual_page_view";
			// Full URL of the new page.
			page_location: string;
			page_title: string;
			// Full URL of the page the visitor navigated from.
			page_referrer: string;
	  }
	| {
			event: "address_search";
			search_type: "address" | "nmi";
			energy: EnergyType;
	  }
	| {
			// The customer submitted an address that could not be turned into a
			// lookup. Their highest-intent moment, and previously invisible.
			//
			// Submit-time only: the autocomplete returning nothing is the normal
			// state while someone is still typing, so tracking that would report
			// ordinary keystrokes as failures.
			event: "address_search_failed";
			// not_resolved: no match for what they typed.
			// search_error: the address service itself failed.
			reason: "not_resolved" | "search_error";
			energy: EnergyType;
	  }
	| {
			// An address matched several NMIs and the customer picked one. Kept
			// separate from address_search so a disambiguating customer is not
			// counted as two searches, which would deflate search -> plan rate.
			event: "address_disambiguated";
			energy: EnergyType;
	  }
	| {
			// Gas only. A plan exists, but the customer is shown the pipeline
			// locator rather than a plan card, and must check their address
			// before anything else happens. Without this the gas funnel goes
			// dark between address_search and plan_viewed.
			event: "gas_availability_check";
			energy: "gas";
	  }
	| {
			// Singular: the plans API returns one plan per lookup.
			event: "plan_viewed";
			energy: EnergyType;
			// PROD1_TEMPLATE, e.g. "SOL-93-SELECT". Matches what the signup
			// payload calls productCode, so the two events join up.
			productCode: string;
			// PROD1, the human-readable plan name, e.g. "Solstice Select".
			product: string;
			isCommercial: boolean;
			promo?: string;
			promo_valid?: boolean;
	  }
	| {
			// Address search completed but nothing was offered.
			event: "plans_unavailable";
			energy: EnergyType;
			// gas_unavailable: a plan exists, but the pipeline locator found no
			// gas connection at the address, so no card is ever shown.
			reason: "no_plan" | "lookup_failed" | "gas_unavailable";
	  }
	| {
			event: "plan_selected";
			energy: EnergyType;
			productCode: string;
			product: string;
			promo?: string;
	  }
	| {
			// First focus on any field of the onboarding form. Fires once per
			// mount. This is the audience seed for abandoned onboarding: someone
			// with this event and no signup_complete started and gave up.
			event: "onboarding_started";
			energy: EnergyType;
			productCode: string;
	  }
	| {
			// First focus within a section. Once per section per mount. The
			// section ids are constants, not the visible legends, so a copy change
			// cannot silently rename a funnel stage — and they carry over to
			// step_viewed when the multi-step form lands.
			event: "onboarding_section_started";
			section_id: OnboardingSectionId;
			section_index: number;
			energy: EnergyType;
	  }
	| {
			// Validation failed on a submit attempt. Field names only — never
			// values, which are the customer's identity documents.
			event: "form_error";
			field_names: string;
			error_count: number;
			energy: EnergyType;
	  }
	| {
			// Fired when /signup/apply returns 200, before the confirmation-page
			// navigation. Carries no PII: no name, email, phone, DOB, address,
			// NMI or life-support status.
			event: "signup_complete";
			// Server-issued dedup key, so a refresh cannot double-count the
			// conversion across ad platforms.
			transaction_id: string;
			energy: EnergyType;
			productCode: string;
			product: string;
			promo?: string;
			isCommercial: boolean;
	  };

/**
 * Query params safe to keep in a tracked URL.
 *
 * Everything else is dropped. Lookup URLs no longer carry property details, and
 * proxy.ts redirects legacy links that do, but the allowlist keeps an address
 * or NMI out of the dataLayer if one is ever added back.
 */
const ALLOWED_QUERY_PARAMS = ["energy", "promo"];

/**
 * Reduce a URL to its origin and pathname plus allowlisted query params.
 *
 * Accepts absolute URLs, or paths resolved against the current origin. Returns
 * an empty string for anything unparseable — an off-site referrer is expected
 * and not worth throwing over.
 */
export function sanitiseUrl(url: string): string {
	if (!url) {
		return "";
	}

	let parsed: URL;
	try {
		parsed = new URL(url, window.location.origin);
	} catch {
		return "";
	}

	const allowed = new URLSearchParams();
	for (const param of ALLOWED_QUERY_PARAMS) {
		const value = parsed.searchParams.get(param);
		if (value) {
			allowed.append(param, value);
		}
	}

	const query = allowed.toString();
	const base = `${parsed.origin}${parsed.pathname}`;
	return query ? `${base}?${query}` : base;
}

/**
 * GA4 truncates an event parameter at 100 characters.
 */
const MAX_PARAM_LENGTH = 100;

/**
 * Join field names into a single parameter without being silently truncated.
 *
 * A failed submit on the onboarding form can produce twenty or more errors,
 * which comfortably exceeds the parameter limit — and GA4 would cut the string
 * mid-name, producing a field that never existed. Instead, send as many whole
 * names as fit, in form order, and rely on `error_count` for the true total.
 */
export function joinFieldNames(names: string[]): string {
	let joined = "";

	for (const name of names) {
		const next = joined ? `${joined},${name}` : name;
		if (next.length > MAX_PARAM_LENGTH) {
			break;
		}
		joined = next;
	}

	return joined;
}

/**
 * Vercel exposes NEXT_PUBLIC_VERCEL_ENV only when "Automatically expose System
 * Environment Variables" is enabled on the project. The NODE_ENV fallback keeps
 * the field populated either way.
 */
function getEnvironment(): string {
	return (
		process.env.NEXT_PUBLIC_VERCEL_ENV ??
		(process.env.NODE_ENV === "production" ? "production" : "development")
	);
}

/**
 * Dimensions that describe the site rather than any one event.
 */
function getPageContext() {
	return {
		site: isVictoria ? "VIC" : "TAS",
		environment: getEnvironment(),
	};
}

/**
 * Inline script that seeds the dataLayer before the GTM container loads.
 *
 * Rendered into the page source by the root layout, so site and environment
 * are set when the container initialises and are available to the genuine page
 * view and anything else firing on load.
 */
export function getInitialDataLayerScript(): string {
	return `window.dataLayer=window.dataLayer||[];window.dataLayer.push(${JSON.stringify(getPageContext())});`;
}

/**
 * Push an event to the dataLayer, with the standard dimensions attached.
 *
 * No-ops on the server so this is safe to import anywhere.
 *
 * Never throws. dataLayer is a plain array shared with every tag in the
 * container, so a badly behaved Custom HTML tag can replace or poison it. Call
 * sites include the signup submit handler, where an exception would strand a
 * customer whose signup had already succeeded — analytics must not be able to
 * break the app.
 */
export function track(event: AnalyticsEvent) {
	if (typeof window === "undefined") {
		return;
	}

	try {
		sendGTMEvent({
			...event,
			...getPageContext(),
		});
	} catch (error) {
		console.error("Failed to push analytics event", event.event, error);
	}
}
